"use client";

import { throttle } from "lodash";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

interface ScrollShadowState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

interface UseScrollShadowOptions {
  /**
   * Selector to find the scrollable element within the container.
   * If not provided and autoDetect is false, the container ref element itself is used.
   */
  scrollableSelector?: string;
  /**
   * Automatically detect the first horizontally scrollable child element.
   * Takes precedence over scrollableSelector if both are provided.
   * @default true
   */
  autoDetect?: boolean;
  /**
   * Throttle time in ms for scroll/resize updates.
   * @default 50
   */
  throttleTime?: number;
}

interface UseScrollShadowReturn<T extends HTMLElement> extends ScrollShadowState {
  containerRef: React.RefObject<T | null>;
}

/**
 * Finds the first element that can scroll horizontally within a container.
 * Checks for overflow-x: auto/scroll and whether content exceeds container width.
 */
function findScrollableElement(container: HTMLElement): HTMLElement | null {
  // Check if container itself is scrollable
  const style = getComputedStyle(container);
  const overflowX = style.overflowX;
  if ((overflowX === "auto" || overflowX === "scroll") && container.scrollWidth > container.clientWidth) {
    return container;
  }

  // Search children recursively (breadth-first)
  const queue: HTMLElement[] = Array.from(container.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement,
  );

  while (queue.length > 0) {
    const element = queue.shift()!;
    const elementStyle = getComputedStyle(element);
    const elementOverflowX = elementStyle.overflowX;

    if (elementOverflowX === "auto" || elementOverflowX === "scroll") {
      return element;
    }

    // Add children to queue
    for (const child of element.children) {
      if (child instanceof HTMLElement) {
        queue.push(child);
      }
    }
  }

  return null;
}

export const useScrollShadow = <T extends HTMLElement = HTMLElement>(
  options: UseScrollShadowOptions = {},
): UseScrollShadowReturn<T> => {
  const { scrollableSelector, autoDetect = true, throttleTime = 50 } = options;
  const containerRef = useRef<T>(null);
  const [scrollState, setScrollState] = useState<ScrollShadowState>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback((element: HTMLElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = element;

    // Small threshold to account for sub-pixel rounding
    const threshold = 1;

    const canScrollLeft = scrollLeft > threshold;
    const canScrollRight = scrollLeft + clientWidth < scrollWidth - threshold;

    setScrollState((prev) => {
      if (prev.canScrollLeft === canScrollLeft && prev.canScrollRight === canScrollRight) {
        return prev;
      }
      return { canScrollLeft, canScrollRight };
    });
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollableElement: HTMLElement | null = null;

    if (autoDetect) {
      scrollableElement = findScrollableElement(container);
    } else if (scrollableSelector) {
      scrollableElement = container.querySelector<HTMLElement>(scrollableSelector);
    } else {
      scrollableElement = container;
    }

    if (!scrollableElement) return;

    const throttledUpdate = throttle(() => {
      updateScrollState(scrollableElement);
    }, throttleTime);

    // Listen for scroll events
    scrollableElement.addEventListener("scroll", throttledUpdate, { passive: true });

    // Listen for resize events on the scrollable element
    const resizeObserver = new ResizeObserver(throttledUpdate);
    resizeObserver.observe(scrollableElement);

    // Also observe content changes that might affect scrollability
    const mutationObserver = new MutationObserver(throttledUpdate);
    mutationObserver.observe(scrollableElement, {
      childList: true,
      subtree: true,
    });

    // Initial check
    throttledUpdate();

    return () => {
      scrollableElement.removeEventListener("scroll", throttledUpdate);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      throttledUpdate.cancel();
    };
  }, [scrollableSelector, throttleTime, updateScrollState, autoDetect]);

  return {
    containerRef,
    ...scrollState,
  };
};
